import React from "react";
import { getLatestTelemetry } from "../api/telemetryApi";
import {
  completeDripSession,
  createDripSession,
  getDripSessions,
} from "../api/dripSessionsApi";

const POLL_INTERVAL_MS = 2000;
const NO_DATA_TIMEOUT_MS = 20000;

function normalizeRows(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

function isSessionActive(status) {
  const value = String(status || "").toUpperCase();
  return value === "ACTIVE" || value === "PAUSED";
}

export default function useAutoLiveSession() {
  const [telemetryRows, setTelemetryRows] = React.useState([]);
  const [sessionRows, setSessionRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [isLiveConnected, setIsLiveConnected] = React.useState(false);
  const [currentSessionId, setCurrentSessionId] = React.useState(null);
  const [lastSeenTime, setLastSeenTime] = React.useState("");

  const lastTelemetryMsRef = React.useRef(null);
  const activeSessionIdRef = React.useRef(null);
  const creatingRef = React.useRef(false);
  const completingRef = React.useRef(false);

  const fetchSessions = React.useCallback(async () => {
    const data = await getDripSessions();
    const rows = normalizeRows(data);
    setSessionRows(rows);

    const activeRow = rows.find((row) => isSessionActive(row.status));
    const sessionId = activeRow ? activeRow.session_id || activeRow.drip_session_id || activeRow.id : null;
    activeSessionIdRef.current = sessionId;
    setCurrentSessionId(sessionId);
  }, []);

  const buildCreatePayload = React.useCallback((telemetryItem) => {
    return {
      admission: telemetryItem?.admission_id ?? telemetryItem?.admission ?? null,
      channel: telemetryItem?.channel_id ?? telemetryItem?.channel ?? null,
      started_by_user: null,
      fluid_label: telemetryItem?.fluid_label || "Live Fluid",
      target_mL_per_hr: telemetryItem?.flow_out ?? null,
      drop_factor_gtt_per_mL: null,
      patient_name: telemetryItem?.patient_name || "Live Patient",
      patient_uid:
        telemetryItem?.patient_uid ||
        telemetryItem?.uhid ||
        telemetryItem?.patient_id ||
        "LIVE-UHID-001",
      fluid_serial_number:
        telemetryItem?.fluid_serial_number ||
        telemetryItem?.device_serial_number ||
        telemetryItem?.device_id ||
        "LIVE-FLUID-001",
      hospital_name: telemetryItem?.hospital_name || "Live Hospital",
    };
  }, []);

  const createSessionIfNeeded = React.useCallback(
    async (telemetryItem) => {
      if (creatingRef.current || activeSessionIdRef.current) return;

      try {
        creatingRef.current = true;
        const created = await createDripSession(buildCreatePayload(telemetryItem));
        const createdId = created?.session_id || created?.drip_session_id || created?.id || null;

        activeSessionIdRef.current = createdId;
        setCurrentSessionId(createdId);
        await fetchSessions();
      } finally {
        creatingRef.current = false;
      }
    },
    [buildCreatePayload, fetchSessions]
  );

  const completeSessionIfNeeded = React.useCallback(async () => {
    if (!activeSessionIdRef.current || completingRef.current) return;

    try {
      completingRef.current = true;
      await completeDripSession(activeSessionIdRef.current);
      activeSessionIdRef.current = null;
      setCurrentSessionId(null);
      await fetchSessions();
    } finally {
      completingRef.current = false;
    }
  }, [fetchSessions]);

  const refreshAll = React.useCallback(async () => {
    try {
      const telemetry = await getLatestTelemetry();
      const rows = Array.isArray(telemetry) ? telemetry : [];
      setTelemetryRows(rows);
      setError("");

      if (rows.length > 0) {
        const now = Date.now();
        lastTelemetryMsRef.current = now;
        setIsLiveConnected(true);
        setLastSeenTime(new Date(now).toLocaleString());

        await createSessionIfNeeded(rows[0]);
      } else {
        const now = Date.now();
        if (
          lastTelemetryMsRef.current &&
          now - lastTelemetryMsRef.current >= NO_DATA_TIMEOUT_MS
        ) {
          setIsLiveConnected(false);
          await completeSessionIfNeeded();
        }
      }

      await fetchSessions();
    } catch (err) {
      setError(err?.message || "Failed to load live monitoring data");
    } finally {
      setLoading(false);
    }
  }, [createSessionIfNeeded, completeSessionIfNeeded, fetchSessions]);

  React.useEffect(() => {
    refreshAll();

    const interval = setInterval(() => {
      refreshAll();
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [refreshAll]);

  React.useEffect(() => {
    const timer = setInterval(async () => {
      const now = Date.now();

      if (
        lastTelemetryMsRef.current &&
        now - lastTelemetryMsRef.current >= NO_DATA_TIMEOUT_MS
      ) {
        setIsLiveConnected(false);
        await completeSessionIfNeeded();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [completeSessionIfNeeded]);

  return {
    telemetryRows,
    sessionRows,
    loading,
    error,
    isLiveConnected,
    currentSessionId,
    lastSeenTime,
    refreshAll,
  };
}